/**
 * Orchestration Console Log Connector
 *
 * Subscribes to the Mycelium backbone with the lowest priority and mirrors
 * allow-listed events into `cortex_events_log` so the in-app Console drawer
 * can render a live timeline. Pure observer — never blocks publishers.
 *
 * The brain edge function and the cortex SSE route write their own rows
 * directly via `logEventToConsole(serviceClient, ...)`; this connector only
 * covers events flowing through the in-app backbone.
 *
 * @module console-log.connector
 */

import { logger } from "../../lib/logger";
import { typedSupabase } from "../../lib/typed-supabase";
import { myceliumEventBackbone } from "../mycelium-event-backbone.service";
import type { MyceliumEvent } from "@flowstate/mycelium-core";
import {
  CONSOLE_DOMAIN_ALLOWLIST,
  buildConsoleRow,
  writeConsoleRow,
} from "./index";

const CONNECTOR_NAME = "OrchestrationConsoleLog";

class ConsoleLogConnectorImpl {
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;

    myceliumEventBackbone.subscribe({
      name: CONNECTOR_NAME,
      handler: this.handleEvent.bind(this),
      // Lowest priority — runs after business logic, never blocks
      priority: 1,
    });

    this.initialized = true;
    logger.info("🪟 Orchestration Console log connector ready");
  }

  async dispose(): Promise<void> {
    this.initialized = false;
  }

  /**
   * Fire-and-forget insert. Must never throw.
   */
  private async handleEvent(event: MyceliumEvent): Promise<void> {
    try {
      if (!CONSOLE_DOMAIN_ALLOWLIST.has(event.domain)) return;
      if (!event.orgId) return;

      const row = buildConsoleRow(event, {
        orgId: event.orgId,
        userId: event.userId ?? undefined,
        principalId: event.principalId ?? undefined,
        // correlationId / causationId come from metadata.context if callers set them
        correlationId: (event.metadata?.context?.['correlationId'] as string | undefined),
        causationId: (event.metadata?.context?.['causationId'] as string | undefined),
        source: "backbone",
      });

      if (!row) return;
      await writeConsoleRow(typedSupabase, row);
    } catch (err) {
      // Defensive — writeConsoleRow already swallows; this is belt+braces
      logger.debug("[console-log] connector swallowed error", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

export const consoleLogConnector = new ConsoleLogConnectorImpl();
